import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Box, Breadcrumbs, Button, Card, CardContent, Chip, CircularProgress, Collapse,
  Grid, IconButton, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon, ChevronRight as ChevronRightIcon,
  Domain as EnterpriseIcon, AccountTree as OrgIcon, Map as RegionIcon,
  Business as OfficeIcon, TollOutlined as CreditIcon,
  LocalHospital as ProviderIcon, People as StaffIcon, Visibility as ViewIcon,
  Login as EnterIcon, MedicalServices as PracticeIcon,
} from '@mui/icons-material';
import * as hierarchyApi from '../api/hierarchy';
import { useAuth } from '../contexts/AuthContext';
import { OPEN_LABEL, openLevel } from '../utils/openLevel';
import StructureTree from '../components/StructureTree';


/**
 * The home screen for a session scoped to a LEVEL — enterprise, organisation or region.
 *
 * It answers the two questions that level actually has: what is underneath me, and how
 * are my AI sessions spread across it. Distributing them and listing the people are
 * their own screens; this one is the map.
 *
 * Nothing here is clinical: every number is structure, staffing or sessions, never a
 * patient. Working inside a level or an office beneath you is the Open button on its row.
 */

export const LEVEL = {
  enterprise: { icon: <EnterpriseIcon />, color: '#7c3aed', label: 'Enterprise' },
  organization: { icon: <OrgIcon />, color: '#2563eb', label: 'Organisation' },
  region: { icon: <RegionIcon />, color: '#0891b2', label: 'Region' },
  clinic: { icon: <OfficeIcon />, color: '#059669', label: 'Office' },
};

/** A pool hands sessions down; an office spends them. Two questions, two phrasings. */
export function creditChip(row) {
  const c = row.credits || row;
  if (!c.has_account) return { text: 'No pool', tone: 'default' };
  if (c.unlimited) return { text: 'Unlimited', tone: 'success' };
  if ((row.scope || c.scope) === 'clinic') {
    const left = c.remaining ?? 0;
    return { text: `${left} of ${c.granted ?? 0} left`, tone: left <= 0 ? 'error' : 'default' };
  }
  const available = c.available ?? 0;
  return { text: `${available} to give of ${c.granted ?? 0}`,
           tone: available <= 0 ? 'warning' : 'default' };
}

export function TreeRow({ node, depth, expanded, onToggle, onOpen, onEnter, entering }) {
  const level = node.is_region_practice
    ? { ...LEVEL.clinic, label: 'Region practice', color: LEVEL.region.color }
    : (LEVEL[node.scope] || LEVEL.clinic);
  const kids = node.children || [];
  const open = expanded.has(node.id);
  const chip = creditChip(node);

  return (
    <>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        pl: 1 + depth * 3, pr: 1.5, py: 1,
        borderLeft: `3px solid ${level.color}`,
        opacity: node.is_active === false ? 0.55 : 1,
        borderBottom: '1px solid', borderBottomColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}>
        <IconButton size="small" onClick={() => onToggle(node.id)} disabled={!kids.length}
          sx={{ visibility: kids.length ? 'visible' : 'hidden' }}
          aria-label={open ? `Collapse ${node.name}` : `Expand ${node.name}`}>
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>
        <Box sx={{ color: level.color, display: 'flex' }}>{level.icon}</Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="body2" fontWeight={600} noWrap>{node.name}</Typography>
            <Chip size="small" variant="outlined" label={level.label}
              sx={{ height: 20, fontSize: 11, borderColor: level.color, color: level.color }} />
            {node.is_active === false && (
              <Chip size="small" label="Switched off" sx={{ height: 20, fontSize: 11 }} />
            )}
          </Stack>
          {node.scope !== 'clinic' && (
            <Typography variant="caption" color="text.secondary">
              {node.office_count} office{node.office_count === 1 ? '' : 's'}
            </Typography>
          )}
        </Box>
        <Chip size="small" label={chip.text}
          color={chip.tone === 'default' ? undefined : chip.tone}
          variant={chip.tone === 'default' ? 'outlined' : 'filled'}
          sx={{ height: 22, fontSize: 11 }} />
        {onEnter && OPEN_LABEL[node.scope] && node.is_active !== false && (
          <Tooltip title={node.scope === 'clinic'
            ? `Work inside ${node.name} as its administrator — patients, queue and team`
            : `Work inside ${node.name} as its administrator — its own dashboard, sessions and people`}>
            <Button size="small" variant="outlined" startIcon={<EnterIcon fontSize="small" />}
              disabled={Boolean(entering)} onClick={() => onEnter(node)}
              sx={{ height: 26, fontSize: 12, whiteSpace: 'nowrap' }}>
              {entering === node.id ? 'Opening…'
                : (node.is_region_practice ? 'Open practice' : OPEN_LABEL[node.scope])}
            </Button>
          </Tooltip>
        )}
        <Tooltip title={`Look inside ${node.name}`}>
          <IconButton size="small" onClick={() => onOpen(node)} aria-label={`Open ${node.name}`}>
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      {!!kids.length && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          {kids.map((child) => (
            <TreeRow key={child.id} node={child} depth={depth + 1} expanded={expanded}
              onToggle={onToggle} onOpen={onOpen} onEnter={onEnter} entering={entering} />
          ))}
        </Collapse>
      )}
    </>
  );
}

function Stat({ value, label, icon, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderTop: `3px solid ${color}`, height: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <Box sx={{ color, display: 'flex' }}>{icon}</Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
      </Stack>
      <Typography variant="h4" fontWeight={700}>{value}</Typography>
    </Paper>
  );
}

export default function NodeOverview() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(null);
  // Anyone may open anything beneath them and work there as its administrator: an
  // enterprise admin its organisations, regions and offices, a region admin its
  // offices. The same audited handoff an organisation admin's Sites page uses.
  // A platform administrator inside a level may open what is beneath it too — a fresh,
  // audited entry, which they could equally make from the admin portal.
  const canEnter = ['enterprise_admin', 'org_admin', 'region_admin'].includes(user?.role);
  const enterNode = async (node) => {
    setEntering(node.id);
    try {
      // The region practice comes back from its own endpoint without a scope; it is an office.
      await openLevel({ ...node, scope: node.scope || 'clinic' });
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not open ${node.name}`);
      setEntering(null);
    }
  };
  // The region's own practice: created the first time it is opened, then entered like
  // any office. Where the region's own providers see patients and record sessions.
  const openPractice = async () => {
    setEntering('practice');
    try {
      const p = await hierarchyApi.openRegionPractice();
      if (p.created) toast.success(`${p.name} is set up`);
      await enterNode(p);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open the region practice');
      setEntering(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hierarchyApi.getTree();
      setData(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load your account');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pool = useMemo(() => (data ? creditChip(data.node) : null), [data]);

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!data) return null;

  const { node, ancestors = [], children = [], totals = {} } = data;
  const level = LEVEL[node.scope] || LEVEL.organization;

  return (
    <Box>
      {!!ancestors.length && (
        <Breadcrumbs sx={{ mb: 1 }}>
          {ancestors.map((a) => (
            <Typography key={a.id} variant="body2" color="text.secondary">{a.name}</Typography>
          ))}
          <Typography variant="body2" fontWeight={600}>{node.name}</Typography>
        </Breadcrumbs>
      )}

      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{ color: level.color, display: 'flex' }}>{level.icon}</Box>
        <Box>
          <Typography variant="h4" fontWeight={700}>{node.name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {level.label} overview
          </Typography>
        </Box>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Stat value={pool.text.split(' ')[0]} label="AI sessions to give"
            icon={<CreditIcon fontSize="small" />} color="#b45309" />
        </Grid>
        <Grid item xs={6} md={3}>
          <Stat value={totals.sessions_used ?? 0} label="Sessions used, all offices"
            icon={<CreditIcon fontSize="small" />} color="#0f766e" />
        </Grid>
        <Grid item xs={6} md={3}>
          <Stat value={totals.providers ?? 0} label="Providers"
            icon={<ProviderIcon fontSize="small" />} color="#059669" />
        </Grid>
        <Grid item xs={6} md={3}>
          <Stat value={totals.staff ?? 0} label="Staff"
            icon={<StaffIcon fontSize="small" />} color="#2563eb" />
        </Grid>
      </Grid>

      {node.scope === 'region' && canEnter && (() => {
        const practice = children.find((ch) => ch.is_region_practice);
        return (
          <Card sx={{ mb: 3, borderLeft: `4px solid ${LEVEL.region.color}` }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ color: LEVEL.region.color, display: 'flex' }}><PracticeIcon /></Box>
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <Typography variant="h6" fontWeight={700}>Region practice</Typography>
                <Typography variant="body2" color="text.secondary">
                  {practice
                    ? 'The region\'s own providers and patients. Open it to see patients, add providers and record sessions — it is funded from this region\'s AI sessions like any office.'
                    : 'Give the region providers and patients of its own, beside its offices. It is set up the first time you open it.'}
                </Typography>
              </Box>
              <Button variant="contained" startIcon={<EnterIcon />} disabled={Boolean(entering)}
                onClick={openPractice}>
                {entering === 'practice' || (practice && entering === practice.id)
                  ? 'Opening…' : (practice ? 'Open region practice' : 'Set up region practice')}
              </Button>
            </CardContent>
          </Card>
        );
      })()}

      <StructureTree />
    </Box>
  );
}
